import TableContainer from "Components/Common/TableContainer";
import CustomButton from "Components/Custom/CustomButton";
import React, { useMemo } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { ItemData } from "types/item.api";

interface CheckMarginModalProps {
  isOpen: boolean;
  onClose: () => void;
  items?: ItemData[];
}

const CheckMarginModal = ({
  isOpen,
  onClose,
  items = [],
}: CheckMarginModalProps) => {
  // -------------------------
  //  CALCULATE ROW DATA
  // -------------------------
  const tableData = useMemo(() => {
    const rows = items.map((item) => {
      const revenue = item.price * item.quantity;
      const costs = item.cost * item.quantity;
      const margin = revenue - costs;
      const marginPercent = revenue > 0 ? (margin / revenue) * 100 : 0;

      return {
        itemName: item.name,
        revenue,
        costs,
        margin,
        marginPercent,
        isTotal: false,
      };
    });

    // Calculate totals
    const totalRevenue = rows.reduce((sum, row) => sum + row.revenue, 0);
    const totalCosts = rows.reduce((sum, row) => sum + row.costs, 0);
    const totalMargin = totalRevenue - totalCosts;
    const totalMarginPercent =
      totalRevenue > 0 ? (totalMargin / totalRevenue) * 100 : 0;

    // Add total row at the end
    rows.push({
      itemName: "Total",
      revenue: totalRevenue,
      costs: totalCosts,
      margin: totalMargin,
      marginPercent: totalMarginPercent,
      isTotal: true,
    });

    return rows;
  }, [items]);

  // -------------------------
  //  TABLE COLUMNS
  // -------------------------
  const columns = useMemo(
    () => [
      {
        header: "ITEM NAME",
        accessorKey: "itemName",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const isTotal = cell.row.original.isTotal;
          return (
            <div>
              <span
                className={`${isTotal ? "fw-semibold" : "fw-normal"
                  } text-black2D fs-14 font-primary`}
              >
                {cell.getValue()}
              </span>
            </div>
          );
        },
      },
      {
        header: "REVENUE",
        accessorKey: "revenue",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const isTotal = cell.row.original.isTotal;
          return (
            <div>
              <span
                className={`${isTotal ? "fw-semibold" : "fw-normal"
                  } text-black2D fs-14 font-primary`}
              >
                £{cell.getValue().toFixed(2)}
              </span>
            </div>
          );
        },
      },
      {
        header: "COSTS",
        accessorKey: "costs",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const isTotal = cell.row.original.isTotal;
          return (
            <div>
              <span
                className={`${isTotal ? "fw-semibold" : "fw-normal"
                  } text-black2D fs-14 font-primary`}
              >
                £{cell.getValue().toFixed(2)}
              </span>
            </div>
          );
        },
      },
      {
        header: "COMMISSION",
        accessorKey: "margin",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const row = cell.row.original;
          const isTotal = row.isTotal;
          return (
            <div>
              <span
                className={`${isTotal ? "fw-semibold" : "fw-normal"
                  } text-black2D fs-14 font-primary`}
              >
                £{row.margin.toFixed(2)} ({row.marginPercent.toFixed(2)}%)
              </span>
            </div>
          );
        },
      },
    ],
    []
  );

  // -------------------------
  //  RENDER MODAL
  // -------------------------
  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      size="md"
      contentClassName="br-7"
    >
      {/* Header */}
      <ModalHeader
        toggle={onClose}
        className="fw-medium fs-16 text-black2D p-3 bg-greyF5 br-7"
      >
        Margin
      </ModalHeader>

      {/* Body */}
      <ModalBody className="p-4">
        <style>
          {`
            .margin-table tbody tr:last-child td {
              border-top: 1px solid #dee2e6 !important;
              padding-top: 1rem !important;
            }
          `}
        </style>
        <div className="table-responsive table-card bg-white mx-n4 mt-2">
          <TableContainer
            columns={columns}
            data={tableData}
            tableClass="align-middle table-nowrap table-borderless margin-table"
            thClass="text-grey89 fw-medium fs-14 font-primary bg-greyF5"
            theadClass="table-light"
            showPagination={false}
            customPageSize={tableData.length}
          />
        </div>

        {/* Divider */}
        <div className="mt-3 mx-n4 dashed mb-0" />
      </ModalBody>

      {/* Footer */}
      <ModalFooter className="justify-content-start mt-0 pt-0">
        <CustomButton
          className="bg-primary text-white br-7 px-3"
          type="button"
          text="Got It!"
          onClick={onClose}
        />
      </ModalFooter>
    </Modal>
  );
};

export default React.memo(CheckMarginModal);
